import React, { useEffect, useState, useMemo } from 'react';
import { Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Button, Skeleton, useDisclosure, Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerCloseButton, DrawerBody, Text, Flex, Badge, Tooltip, SimpleGrid, Stat, StatLabel, StatNumber, Select } from '@chakra-ui/react';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import { useTableDensity } from '../context/TableDensityContext';
import Page from '../components/ui/Page';
import SectionCard from '../components/ui/SectionCard';
import { useParams, useNavigate } from 'react-router-dom';
import { WarningIcon, InfoIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Scatter, ReferenceDot } from 'recharts';
import TestCaseDrawer from '../components/TestCaseDrawer';
import client from '../utils/fetchClient';
import { useProjectContext } from '../context/ProjectContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Dynamic group mapping: group name -> array of keywords (unused - keeping for future reference)
// const GROUP_KEYWORDS = {
//   Assets: [
//     'asset management', 'asset categories', 'asset filters', 'assets', 'asset'
//   ],
//   Expenses: [
//     'expense_settings', 'expenses', 'expense'
//   ],
//   // Add more groups/modules here as needed
// };

function getGroup(tc) {
  // Group by first tag before the first '›' in the title
  if (!tc.title) return 'Unknown';
  const parts = tc.title.split('›');
  if (parts.length > 1) return parts[0].replace(/^@/, '').trim();
  return 'Unknown';
}

function getDisplayTitle(tc) {
  return tc.title ? tc.title.split('›').pop().trim() : '';
}

function getMainGroup(tc) {
  // Extract group name from suite field (e.g., "api/assets/assets.spec.js" -> "assets")
  if (tc.suite) {
    const parts = tc.suite.split('/');
    if (parts.length >= 2) {
      return parts[1]; // Return the second part (e.g., "assets" from "api/assets/assets.spec.js")
    }
  }
  // Fallback to file field if suite is not available
  if (tc.file) {
    const parts = tc.file.split('/');
    if (parts.length >= 2) {
      return parts[1];
    }
  }
  return getGroup(tc);
}

// --- PerformanceTrendChart component ---
function PerformanceTrendChart({ data }) {
  if (!data || data.length === 0) return <Text color="gray.400">No trend data available.</Text>;
  const statusColor = status => {
    if (status === 'passed') return '#38A169'; // green.500
    if (status === 'failed') return '#E53E3E'; // red.500
    if (status === 'skipped') return '#D69E2E'; // yellow.500
    return '#A0AEC0'; // gray.400
  };
  return (
    <Box w="100%" h="220px">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
          <XAxis dataKey="date" tickFormatter={d => new Date(d).toLocaleDateString()} fontSize={12} />
          <YAxis dataKey="duration" fontSize={12} width={60} />
          <Line type="monotone" dataKey="duration" stroke="#3182ce" strokeWidth={2} dot={false} />
          <Scatter data={data} fill="#3182ce">
            {data.map((entry, idx) => (
              <ReferenceDot key={idx} x={entry.date} y={entry.duration} r={6} fill={statusColor(entry.status)} stroke="#fff" strokeWidth={1} />
            ))}
          </Scatter>
          <RechartsTooltip labelFormatter={d => new Date(d).toLocaleString()} formatter={(value, name) => [value, name === 'duration' ? 'Duration (ms)' : name]} />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

// --- Enhanced HistoricalRunsTable component ---
function HistoricalRunsTable({ runs }) {
  if (!runs || runs.length === 0) return <Text color="gray.400">No recent runs available.</Text>;
  const statusColorScheme = status => {
    if (status === 'passed') return 'green';
    if (status === 'failed') return 'red';
    if (status === 'skipped') return 'yellow';
    return 'gray';
  };
  return (
    <Box bg="white" borderRadius="lg" boxShadow="sm" p={3}>
      {/* Desktop/tablet table */}
      <Table size="sm" variant="simple" display={{ base: 'none', md: 'table' }}>
        <Thead>
          <Tr>
            <Th>Date</Th>
            <Th>Status</Th>
            <Th>Duration</Th>
            <Th>Error Message</Th>
          </Tr>
        </Thead>
        <Tbody>
          {runs.map((run, idx) => (
            <Tr key={idx} bg={idx % 2 === 0 ? 'gray.50' : 'white'}>
              <Td>{new Date(run.date).toLocaleString()}</Td>
              <Td>
                <Badge colorScheme={statusColorScheme(run.status)} fontSize="0.9em" px={2} py={1} borderRadius="md">
                  {run.status ? run.status.charAt(0).toUpperCase() + run.status.slice(1) : '-'}
                </Badge>
              </Td>
              <Td>{run.duration != null ? `${run.duration} ms` : '-'}</Td>
              <Td maxW="260px" whiteSpace="normal">
                <Text noOfLines={1} title={run.errorMessage || '-'}>{run.errorMessage || '-'}</Text>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      {/* Mobile stacked cards */}
      <Box display={{ base: 'block', md: 'none' }}>
        {runs.map((run, idx) => (
          <Box key={idx} borderWidth={1} borderColor="gray.200" borderRadius="lg" p={3} mb={3} bg="white">
            <Flex justify="space-between" mb={1}><Text color="gray.500">Date</Text><Text fontWeight="semibold" maxW="60%" textAlign="right">{new Date(run.date).toLocaleString()}</Text></Flex>
            <Flex justify="space-between" mb={1}><Text color="gray.500">Status</Text>
              <Badge colorScheme={statusColorScheme(run.status)} borderRadius="md">{run.status ? run.status.charAt(0).toUpperCase() + run.status.slice(1) : '-'}</Badge>
            </Flex>
            <Flex justify="space-between" mb={2}><Text color="gray.500">Duration</Text><Text>{run.duration != null ? `${run.duration} ms` : '-'}</Text></Flex>
            <Box>
              <Text color="gray.500" fontSize="sm" mb={1}>Error Message</Text>
              <Text fontSize="sm" whiteSpace="normal" wordBreak="break-word">{run.errorMessage || '-'}</Text>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function TestCasesPage() {
  const { density } = useTableDensity();
  const tableSize = density === 'compact' ? 'sm' : 'md';
  const { projects, selectedProject, setSelectedProject, effectiveProjectParam } = useProjectContext();
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen: isStackDrawerOpen, onOpen: onStackDrawerOpen, onClose: onStackDrawerClose } = useDisclosure();
  const { isOpen: isMetricsDrawerOpen, onOpen: onOpenMetricsDrawer, onClose: onCloseMetricsDrawer } = useDisclosure();
  const { group } = useParams();
  const navigate = useNavigate();
  const [metricsDrawerData, setMetricsDrawerData] = useState({ metrics: null, trendData: [], recentRuns: [] });
  const [metricsDrawerLoading, setMetricsDrawerLoading] = useState(false);
  const [metricsDrawerError, setMetricsDrawerError] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState({});

  useEffect(() => {
    // Add cleanup flag to prevent race conditions
    let isCancelled = false;
    
    setLoading(true);
    const params = new URLSearchParams();
    if (effectiveProjectParam) params.append('project', effectiveProjectParam);
    
    // Use group filter to get only test cases for this specific group
    if (group) {
      // For now, just use the group name directly as the filter
      // The backend FilterBuilder will handle partial matching on suite, file, and title
      params.append('group', group);
    }
    
    // Set reasonable pageSize for the specific group
    params.append('pageSize', '100');
    params.append('page', '1');
    
    // Fetch test cases data for the specific group
    client.get(`${API_BASE_URL}/api/test-cases`, { query: Object.fromEntries(params) })
      .then(res => res.json())
      .then(data => {
        if (isCancelled) return;
        
        const testCasesList = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.data)
          ? data.data
          : [];
        
        setTestCases(testCasesList);
        setLoading(false);
      })
      .catch((error) => {
        if (isCancelled) return;
        console.error('Error fetching test cases:', error);
        setTestCases([]);
        setLoading(false);
      });
    
    // Cleanup function
    return () => {
      isCancelled = true;
    };
  }, [group, effectiveProjectParam]);

  // Use the test cases directly since they're already filtered by the API
  const filteredCases = useMemo(() => {
    if (!Array.isArray(testCases)) return [];
    return testCases;
  }, [testCases]);

  // Group filtered cases by module (file)
  const groupedByModule = useMemo(() => {
    const groups = {};
    filteredCases.forEach(tc => {
      const mod = getMainGroup(tc);
      if (!groups[mod]) groups[mod] = [];
      groups[mod].push(tc);
    });
    return groups;
  }, [filteredCases]);

  const handleViewMetrics = async (testcase) => {
    onOpenMetricsDrawer();
    setMetricsDrawerLoading(true);
    setMetricsDrawerError(null);
    setMetricsDrawerData({ metrics: null, trendData: [], recentRuns: [] });
    try {
      const params = new URLSearchParams();
      if (effectiveProjectParam) params.append('project', effectiveProjectParam);
      const res = await client.get(`${API_BASE_URL}/api/test-cases/${testcase.id}/history`, { query: Object.fromEntries(params) });
      if (!res.ok) throw new Error('Failed to fetch metrics');
      const data = await res.json();
      setMetricsDrawerData({
        metrics: data.metrics,
        trendData: data.trendData,
        recentRuns: data.recentRuns
      });
      setMetricsDrawerLoading(false);
    } catch (err) {
      setMetricsDrawerError('Failed to load metrics.');
      setMetricsDrawerLoading(false);
    }
  };

  const handleStackTrace = async (tc) => {
    setSelectedCase(tc);
    setDrawerLoading(true);
    onStackDrawerOpen();
    
    try {
      // Fetch detailed test case information from the test run
      const res = await client.get(`${API_BASE_URL}/api/test-runs/${tc.testRunId}`);
      if (!res.ok) throw new Error('Failed to fetch test run details');
      
      const testRunData = await res.json();
      const detailedTestCase = testRunData.testCases?.find(dtc => dtc.id === tc.id);
      
      if (detailedTestCase) {
        // Update the selected case with detailed information
        setSelectedCase(detailedTestCase);
        
        // Debug logging removed for production
        
        // Fetch AI summary only if we have error data
        const hasErrorData = detailedTestCase.errorMessage || detailedTestCase.errorStack;
        if (hasErrorData) {
          try {
            const aiRes = await client.post(`${API_BASE_URL}/api/ai-summary`, { 
              body: { 
                errorStack: detailedTestCase.errorStack || '', 
                errorMessage: detailedTestCase.errorMessage || '' 
              } 
            });
            if (!aiRes.ok) throw new Error('Failed to fetch AI summary');
            const aiData = await aiRes.json();
            setAiSummary(prev => ({ ...prev, [tc.id]: aiData.summary }));
          } catch (err) {
            setAiSummary(prev => ({ ...prev, [tc.id]: 'Failed to load AI analysis. Please try again.' }));
          }
        } else {
          setAiSummary(prev => ({ ...prev, [tc.id]: 'No error data available for AI analysis.' }));
        }
      } else {
        setAiSummary(prev => ({ ...prev, [tc.id]: 'Detailed test case information not available.' }));
      }
    } catch (err) {
      setAiSummary(prev => ({ ...prev, [tc.id]: 'Failed to load test case details. Please try again.' }));
    }
    
    setDrawerLoading(false);
  };

  if (!group) {
    // If no group param, redirect to /test-case-groups
    navigate('/test-case-groups');
    return null;
  }

  if (loading) {
    return <LoadingState message={`Loading test cases for ${group}`} minH="80vh" />;
  }

  if (!testCases || testCases.length === 0) {
    return <EmptyState emoji="🧪" title="No test cases found" subtitle="This group may not have any test cases in the current period, or the data may not be available." />;
  }

  return (
    <Page>
      <Flex mb={4} fontSize="sm" color="gray.600" gap={2} align="center">
        <Button variant="link" size="sm" onClick={() => navigate('/test-case-groups')}>Test Groups</Button>
        <Text>/</Text>
        <Text fontWeight="semibold">{group}</Text>
      </Flex>
      <Flex justify="space-between" align="center" wrap="wrap" gap={3} mb={4}>
        <Select
          display={{ base: 'block', md: 'none' }}
          value={selectedProject || 'ALL PROJECTS'}
          onChange={e => { setSelectedProject(e.target.value); }}
          maxW="220px"
          bg="white"
        >
          <option value="ALL PROJECTS">All Projects</option>
          {projects.map((p, idx) => (
            <option key={(p.id ?? p) + '-' + idx} value={p.id ?? p}>{p.name ?? p}</option>
          ))}
        </Select>
        <Button
        leftIcon={<ArrowBackIcon />}
        colorScheme="gray"
        variant="ghost"
        mb={4}
        onClick={() => navigate('/test-case-groups')}
        aria-label="Back to Test Groups"
      >
        Back to Test Groups
      </Button>
      </Flex>
      <Heading mb={8} fontWeight="bold" fontSize={{ base: '2xl', md: '3xl' }} color="gray.800">Test Cases & Stack Traces</Heading>
      {loading ? (
        <Skeleton height="40px" width="80%" mb={4} />
      ) : (
        <>
          {Object.entries(groupedByModule).map(([mod, cases], idx, arr) => (
            <React.Fragment key={mod}>
              <SectionCard mb={10}>
                <Heading as="h3" size="md" mb={4} color="green.700">{mod}</Heading>
                {/* Desktop/tablet table */}
                <Box display={{ base: 'none', md: 'block' }}>
                  <Table variant="striped" size={tableSize} bg="white" borderRadius="lg" boxShadow="md" sx={{ th: { position: 'sticky', top: 0, bg: 'white', zIndex: 1 } }}>
                    <Thead>
                      <Tr>
                        <Th>Test Name</Th>
                        <Th>Status</Th>
                        <Th>Error Message</Th>
                        <Th>Stack Trace</Th>
                        <Th>View</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {cases.map(tc => {
                        const status = tc.status ? tc.status.charAt(0).toUpperCase() + tc.status.slice(1) : 'Unknown';
                        let statusColor = 'gray.600';
                        if (tc.status === 'failed') statusColor = 'red.500';
                        else if (tc.status === 'skipped') statusColor = 'yellow.600';
                        else if (tc.status === 'passed') statusColor = 'green.600';
                        return (
                          <Tr
                            key={tc.id}
                            style={{ cursor: 'default' }}
                            tabIndex={0}
                            aria-label={`Test case row for ${tc.title}`}
                          >
                            <Td>{getDisplayTitle(tc)}</Td>
                            <Td color={statusColor} fontWeight="bold">{status}</Td>
                            <Td>{tc.errorMessage || '-'}</Td>
                            <Td>
                              {tc.status === 'failed' ? (
                                <Tooltip label="View stack trace and AI summary" aria-label="Stack trace tooltip">
                                  <Button size="sm" colorScheme="red" leftIcon={<WarningIcon />} aria-label="View Stack Trace" onClick={() => handleStackTrace(tc)}>
                                    Stack Trace
                                  </Button>
                                </Tooltip>
                              ) : (
                                <Text color="gray.400">-</Text>
                              )}
                            </Td>
                            <Td>
                              <Tooltip label="View test case metrics and history" aria-label="Metrics tooltip">
                                <Button size="sm" colorScheme="blue" leftIcon={<InfoIcon />} aria-label="View Metrics" onClick={() => handleViewMetrics(tc)}>
                                  Metrics
                                </Button>
                              </Tooltip>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                   </Table>
                </Box>
                {/* Mobile stacked cards */}
                <Box display={{ base: 'block', md: 'none' }}>
                  {cases.map((tc, index) => {
                    const status = tc.status ? tc.status.charAt(0).toUpperCase() + tc.status.slice(1) : 'Unknown';
                    let statusColor = 'gray.600';
                    if (tc.status === 'failed') statusColor = 'red.500';
                    else if (tc.status === 'skipped') statusColor = 'yellow.600';
                    else if (tc.status === 'passed') statusColor = 'green.600';
                    return (
                      <Box key={tc.id || index} borderWidth={1} borderColor="gray.200" borderRadius="lg" p={3} mb={3} bg="white">
                        <Flex justify="space-between" mb={1}><Text color="gray.500">Test Name</Text><Text fontWeight="semibold" maxW="60%" textAlign="right">{getDisplayTitle(tc)}</Text></Flex>
                        <Flex justify="space-between" mb={1}><Text color="gray.500">Status</Text><Text fontWeight="bold" color={statusColor}>{status}</Text></Flex>
                        <Box mt={2} mb={3}>
                          <Text color="gray.500" fontSize="sm" mb={1}>Error Message</Text>
                          <Text fontSize="sm" noOfLines={3}>{tc.errorMessage || '-'}</Text>
                        </Box>
                        <Flex gap={2} wrap="wrap">
                          {tc.status === 'failed' ? (
                            <Button size="sm" colorScheme="red" leftIcon={<WarningIcon />} aria-label="View Stack Trace" onClick={() => handleStackTrace(tc)}>
                              Stack Trace
                            </Button>
                          ) : (
                            <Button size="sm" isDisabled>Stack Trace</Button>
                          )}
                          <Button size="sm" colorScheme="blue" leftIcon={<InfoIcon />} aria-label="View Metrics" onClick={() => handleViewMetrics(tc)}>
                            Metrics
                          </Button>
                        </Flex>
                      </Box>
                    );
                  })}
                </Box>
               </SectionCard>
              {idx < arr.length - 1 && <hr style={{ border: '1px solid #e2e8f0', margin: '32px 0' }} />}
            </React.Fragment>
          ))}
        </>
      )}
      <TestCaseDrawer
        open={isStackDrawerOpen}
        onClose={() => { onStackDrawerClose(); setSelectedCase(null); }}
        testcase={selectedCase}
        loading={drawerLoading}
        aiSummary={selectedCase ? aiSummary[selectedCase.id] : null}
      />
      <Drawer isOpen={isMetricsDrawerOpen} placement={{ base: 'bottom', md: 'right' }} onClose={onCloseMetricsDrawer} size={{ base: 'full', md: 'lg' }}>
        <DrawerOverlay />
        <DrawerContent bg="white">
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" bg="blue.100">Test Case Metrics</DrawerHeader>
          <DrawerBody bg="gray.50" p={0}>
            <Flex direction="column" align="center" w="100%" minH="100%" p={{ base: 2, md: 6 }}>
              {metricsDrawerLoading ? (
                <Box p={4} w="100%"><Skeleton height="32px" mb={4} /><Skeleton height="200px" mb={4} /><Skeleton height="180px" /></Box>
              ) : metricsDrawerError ? (
                <Box color="red.500" p={4}>{metricsDrawerError}</Box>
              ) : metricsDrawerData.metrics ? (
                <>
                  {/* Summary Metrics Section */}
                  <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" p={6} mb={8} w="100%" maxW="720px">
                    <Heading size="md" mb={6} color="blue.800" fontWeight="bold">Summary Metrics</Heading>
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={8}>
                      {/* Pass Rate with color logic */}
                      <Stat bg={((metricsDrawerData.metrics.passRate * 100) > 80) ? 'green.50' : ((metricsDrawerData.metrics.passRate * 100) > 50 ? 'yellow.50' : 'red.50')} borderRadius="md" p={2}>
                        <StatLabel color="gray.500">Pass Rate</StatLabel>
                        <StatNumber fontSize="3xl" fontWeight="bold" color={((metricsDrawerData.metrics.passRate * 100) > 80) ? 'green.500' : ((metricsDrawerData.metrics.passRate * 100) > 50 ? 'yellow.500' : 'red.500')}>
                          {(metricsDrawerData.metrics.passRate * 100).toFixed(1)}%
                        </StatNumber>
                      </Stat>
                      {/* Avg Duration */}
                      <Stat>
                        <StatLabel color="gray.500">Avg Duration</StatLabel>
                        <StatNumber fontSize="3xl" fontWeight="bold" color="blue.600">{Math.round(metricsDrawerData.metrics.avgDuration)} ms</StatNumber>
                      </Stat>
                      {/* Failures */}
                      <Stat>
                        <StatLabel color="gray.500">Failures</StatLabel>
                        <StatNumber fontSize="3xl" fontWeight="bold" color="red.500">{metricsDrawerData.metrics.failCount}</StatNumber>
                      </Stat>
                      {/* Min Duration */}
                      <Stat>
                        <StatLabel color="gray.500">Min Duration</StatLabel>
                        <StatNumber fontSize="xl" fontWeight="bold" color="blue.600">{Math.round(metricsDrawerData.metrics.minDuration)} ms</StatNumber>
                      </Stat>
                      {/* Max Duration */}
                      <Stat>
                        <StatLabel color="gray.500">Max Duration</StatLabel>
                        <StatNumber fontSize="xl" fontWeight="bold" color="blue.600">{Math.round(metricsDrawerData.metrics.maxDuration)} ms</StatNumber>
                      </Stat>
                      {/* Passes */}
                      <Stat>
                        <StatLabel color="gray.500">Passes</StatLabel>
                        <StatNumber fontSize="xl" fontWeight="bold" color="green.600">{metricsDrawerData.metrics.passCount}</StatNumber>
                      </Stat>
                      {/* Skips */}
                      <Stat>
                        <StatLabel color="gray.500">Skips</StatLabel>
                        <StatNumber fontSize="xl" fontWeight="bold" color="yellow.600">{metricsDrawerData.metrics.skipCount}</StatNumber>
                      </Stat>
                      {/* Flakiness with color logic */}
                      <Stat>
                        <StatLabel color="gray.500">Flakiness</StatLabel>
                        <StatNumber fontSize="xl" fontWeight="bold" color={(metricsDrawerData.metrics.flakinessScore * 100) > 10 ? 'orange.500' : 'green.500'}>
                          {(metricsDrawerData.metrics.flakinessScore * 100).toFixed(1)}%
                        </StatNumber>
                      </Stat>
                      {/* First Run */}
                      <Stat>
                        <StatLabel color="gray.500">First Run</StatLabel>
                        <StatNumber fontSize="md" fontWeight="bold" color="gray.600">{metricsDrawerData.metrics.firstRunAt ? new Date(metricsDrawerData.metrics.firstRunAt).toLocaleString() : '-'}</StatNumber>
                      </Stat>
                      {/* Last Run */}
                      <Stat>
                        <StatLabel color="gray.500">Last Run</StatLabel>
                        <StatNumber fontSize="md" fontWeight="bold" color="gray.600">{metricsDrawerData.metrics.lastRunAt ? new Date(metricsDrawerData.metrics.lastRunAt).toLocaleString() : '-'}</StatNumber>
                      </Stat>
                    </SimpleGrid>
                  </Box>
                  {/* Performance Trend Section */}
                  <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" p={6} mb={8} w="100%" maxW="720px">
                    <Heading size="md" mb={6} color="blue.800" fontWeight="bold">Performance Trend</Heading>
                    <Box w="100%" h="240px">
                      <PerformanceTrendChart data={metricsDrawerData.trendData} />
                    </Box>
                  </Box>
                  {/* Recent Runs Section */}
                  <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" p={6} mb={2} w="100%" maxW="720px">
                    <Heading size="md" mb={6} color="blue.800" fontWeight="bold">Recent Runs</Heading>
                    <HistoricalRunsTable runs={metricsDrawerData.recentRuns} />
                  </Box>
                </>
              ) : null}
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Page>
  );
}

export default TestCasesPage; 